import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCreditCard, faEarthAmericas, faFlag, faPlus, faTrophy } from "@fortawesome/pro-regular-svg-icons";
import React, { useState } from "react";
import classNames from "classnames";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { ILocation, ISegment } from "redux/reducers/models/reservations";
import { MoveBookingToDropSegmentAction, useDropBookedSegment } from "./useDropBookedSegment";
import RightClickMenu from "components/rightClickMenu/RightClickMenu";

export interface ISegmentSlotFlag {
  webBooking: boolean;
  creditCard: boolean;
}

export function SegmentSlotFlags(props: { flags?: Array<ISegmentSlotFlag> }) {
  const flags = props.flags ?? [];
  if (!flags.some(flag => flag.webBooking || flag.creditCard)) {
    return null;
  }

  return (
    <div className="segment-slot-flags">
      {flags.map((flag, index) => (
        <div className="segment-slot-flags__row" key={index}>
          {flag.webBooking ? <FontAwesomeIcon icon={faEarthAmericas} /> : null}
          {flag.creditCard ? <FontAwesomeIcon icon={faCreditCard} /> : null}
        </div>
      ))}
    </div>
  );
}

interface IOpenSegmentBand {
  kind: "league" | "tournament";
  title: string;
  tag: string;
  timeLabel: string;
}

interface IOpenSegmentProps {
  cellClassNames: string;
  cellContentClassName: string;
  rowSpan?: number;
  dimmed?: boolean;
  band?: IOpenSegmentBand;
  slotFlags?: Array<ISegmentSlotFlag>;
  segment: ISegment;
  location: ILocation;
  availableDropHours: number;
  moveBookingToDropSegment: MoveBookingToDropSegmentAction;
  openBookingModal: (segment: ISegment, location: ILocation) => void;
  setBlockedType: (segment: ISegment, status: string) => Promise<void>;
  updateWebBooking: (segment: ISegment) => Promise<void>;
  updateCreditCardRequired: (segment: ISegment) => Promise<void>;
  deleteSegment: (segment: ISegment) => void;
  viewAuditTrail: (segmentId: number) => void;
  onSelectSlot?: (index: number) => void;
}

export default function OpenSegment(props: IOpenSegmentProps) {
  const {
    cellClassNames,
    cellContentClassName,
    rowSpan = 1,
    dimmed,
    band,
    slotFlags,
    segment,
    location,
    availableDropHours,
    moveBookingToDropSegment,
    openBookingModal,
    setBlockedType,
    updateWebBooking,
    updateCreditCardRequired,
    deleteSegment,
    viewAuditTrail,
    onSelectSlot,
  } = props;

  const { t } = useTranslation();
  const [hoveredSlot, setHoveredSlot] = useState<number | null>(null);
  const flags = slotFlags?.length
    ? slotFlags
    : [{ webBooking: Boolean(segment.web_booking_enabled), creditCard: Boolean(segment.credit_card_required) }];
  const activeSlot = hoveredSlot ?? 0;
  const activeFlags = flags[activeSlot];
  const activeTime = moment(segment.start_time, ["HH:mm:ss", "HH:mm"]).add(activeSlot * 30, "minutes").format("hh:mm A");
  const showHoverIcons = Boolean(activeFlags?.webBooking && activeFlags?.creditCard);
  const hoverDetails = showHoverIcons
    ? []
    : [
      activeFlags?.webBooking ? t("Online booking") : "",
      activeFlags?.creditCard ? t("Pre-payment required") : "",
    ].filter(Boolean);
  const hoverLabel = [activeTime, ...hoverDetails].join(" • ");
  const showHoverPlus = !showHoverIcons && hoverDetails.length === 0;
  const hideHeader = Boolean(band && hoveredSlot === 0);

  function hoverSlotFromEvent(event: React.MouseEvent<HTMLElement>) {
    if (rowSpan <= 1) {
      setHoveredSlot(0);
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    const index = Math.min(
      rowSpan - 1,
      Math.max(0, Math.floor((event.clientY - rect.top) / (rect.height / rowSpan))),
    );
    setHoveredSlot(index);
  }

  const useDropBookedSegmentState = useDropBookedSegment(
    segment,
    location,
    availableDropHours,
    moveBookingToDropSegment,
  );

  return (
    <>
      <td
        ref={useDropBookedSegmentState.drop}
        rowSpan={rowSpan}
        className={classNames(cellClassNames, { "segment-slot--span": rowSpan > 1 })}
        style={{
          boxShadow:
            useDropBookedSegmentState.isOver &&
            `0px 0px 0px 1px ${useDropBookedSegmentState.canDrop ? "green" : "red"} inset`,
        }}
        onMouseEnter={hoverSlotFromEvent}
        onMouseMove={hoverSlotFromEvent}
        onMouseLeave={() => setHoveredSlot(null)}
        onClick={event => {
          if (!onSelectSlot || rowSpan <= 1) {
            void openBookingModal(segment, location);
            return;
          }
          const rect = event.currentTarget.getBoundingClientRect();
          const index = Math.min(
            rowSpan - 1,
            Math.max(0, Math.floor((event.clientY - rect.top) / (rect.height / rowSpan))),
          );
          onSelectSlot(index);
        }}
      >
        <div className="segment-booking-frame" style={rowSpan > 1 ? { height: rowSpan * 52 } : undefined}>
        <RightClickMenu
          fullHeightWidth
          sections={[
            [
              {
                icon: "circle",
                title: t("secure.facility.reservation.reservation_drag_and_drop_elements.open_segment.001"),

                action: () => setBlockedType(segment, "open"),
                disabled: segment.blocked_type === "open",
              },
              {
                icon: "ban",
                title: t("secure.facility.reservation.reservation_drag_and_drop_elements.open_segment.002"),
                action: () => setBlockedType(segment, "blocked"),

                disabled: String(segment.blocked_type) === "blocked",
              },
            ],
            [
              {
                icon: "globe",
                title: segment.web_booking_enabled
                  ? t("secure.facility.reservation.reservation_drag_and_drop_elements.open_segment.003")
                  : t("secure.facility.reservation.reservation_drag_and_drop_elements.open_segment.004"),
                action: () => updateWebBooking(segment),
              },
              {
                icon: "credit-card",
                title: segment.credit_card_required
                  ? t("secure.facility.reservation.reservation_drag_and_drop_elements.open_segment.005")
                  : t("secure.facility.reservation.reservation_drag_and_drop_elements.open_segment.006"),
                action: () => updateCreditCardRequired(segment),
              },
            ],
            [
              {
                icon: "list",
                title: t("secure.facility.reservation.reservation_drag_and_drop_elements.open_segment.007"),
                action: () => viewAuditTrail(segment.id),
              },
              {
                icon: "trash",
                title: t("secure.facility.reservation.reservation_drag_and_drop_elements.open_segment.008"),
                action: () => deleteSegment(segment),
              },
            ],
          ]}
        >
          <div className={cellContentClassName}>
            {band ? (
              <div className={classNames("schedule-band", {
                "schedule-band--compact": rowSpan <= 1,
                "schedule-band--dimmed": dimmed,
                "schedule-band--header-hidden": hideHeader,
              })}>
                <div className="schedule-band-main">
                  <div className="schedule-band-details">
                    <div className="schedule-band-heading">
                      <div className="schedule-band-title">
                        <FontAwesomeIcon icon={band.kind === "league" ? faTrophy : faFlag} className="schedule-band-icon" />
                        <span>{band.title}</span>
                      </div>
                    </div>
                    {rowSpan > 1 ? <span className="schedule-band-tag">{band.tag}</span> : null}
                  </div>
                  {rowSpan > 1 ? <p className="schedule-band-time">{band.timeLabel}</p> : null}
                </div>
              </div>
            ) : (
              <div className="segment-slot-hit" />
            )}
          </div>
        </RightClickMenu>
        <SegmentSlotFlags flags={flags} />
        <div
          className={classNames("segment-slot-hover", {
            "segment-slot-hover--text": !showHoverPlus && !showHoverIcons,
            "segment-slot-hover--icons": showHoverIcons,
            "is-visible": hoveredSlot !== null,
          })}
          style={hoveredSlot !== null && rowSpan > 1 ? { top: hoveredSlot * 52 + 26 } : undefined}
        >
          {showHoverPlus || showHoverIcons ? <FontAwesomeIcon icon={faPlus} /> : null}
          <span>{showHoverIcons ? activeTime : hoverLabel}</span>
          {showHoverIcons ? (
            <>
              <FontAwesomeIcon className="segment-slot-hover-icon" icon={faEarthAmericas} />
              <FontAwesomeIcon className="segment-slot-hover-icon" icon={faCreditCard} />
            </>
          ) : null}
        </div>
        </div>
      </td>

      {useDropBookedSegmentState.dropConfirmationPopup}
    </>
  );
}
